import React, { useEffect, useState } from "react";
import { MessageCircle, Menu, X } from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const links = [
  { href: "#system", label: "System" },
  { href: "#packages", label: "Packages" },
  { href: "#proof", label: "Proof" },
  { href: "#use-cases", label: "Use Cases" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={
        scrolled
          ? {
              backgroundColor: "rgba(31, 35, 48, 0.95)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 8px 32px -12px rgba(0,0,0,0.4)",
            }
          : { backgroundColor: "transparent" }
      }
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand"
          className="text-white font-display text-2xl tracking-tight flex items-baseline gap-1.5"
        >
          Conversio
          <span className="font-display-italic text-lg">™</span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-cream/70 hover:text-[var(--gold-light)] text-sm font-medium transition-colors"
              style={{ color: "rgba(248,241,231,0.75)" }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.03]"
            style={{ background: "var(--whatsapp)" }}
          >
            <MessageCircle size={16} strokeWidth={2.5} />
            Chat on WhatsApp
          </a>
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-[1.03]"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            Book Strategy Call
          </a>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[var(--ink)] border-t border-white/5 px-6 py-6 space-y-4"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-cream/75 text-base"
              style={{ color: "rgba(248,241,231,0.75)" }}
            >
              {l.label}
            </a>
          ))}
          <div className="flex flex-col gap-3 pt-2">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white"
              style={{ background: "var(--whatsapp)" }}
            >
              <MessageCircle size={16} strokeWidth={2.5} />
              Chat on WhatsApp
            </a>
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              Book Strategy Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
